import { useJumboDialog } from '@jumbo/components/JumboDialog/hooks/useJumboDialog';
import useJumboAuth from '@jumbo/hooks/useJumboAuth';
import ArticleIcon from "@mui/icons-material/Article";
import { Button, Pagination } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { getAllOpExpensesData, getAllOpExpensessCleanUp } from 'app/redux/actions/subTaskAction';
import subTaskService from 'app/services/subtask_service';
import moment from 'moment';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import ExpenseDocumentBox from '../HrOfficeExpanses/ExpenseDocumentBox';
import { useSearchParams } from 'react-router-dom'; // Import for URL params
import { docBaseUrl } from 'app/services/config';

// ---

const LIMIT = 10;

/**
 * Parses the DataGridPro filterModel into a query string for the API.
 * @param {object} filterModel - The filter model from DataGridPro.
 * @returns {string} The query string fragment.
 */
const buildFilterString = (filterModel) => {
    if (!filterModel?.items) return '';

    return filterModel.items
        .filter(item => item.field !== undefined)
        .map(item => `&${item.field}=${item.value || ''}`)
        .join('');
};

/**
 * Extracts a specific filter value from the URL search params.
 * @param {URLSearchParams} searchParams - The current URL search parameters.
 * @param {string} field - The field name to look for.
 * @returns {string | undefined} The filter value or undefined.
 */
const getFilterValueFromParams = (searchParams, field) => {
    const value = searchParams.get(`filter_${field}`);
    return value === null ? undefined : value;
};

// ---

export default function AllOpExpenses() {
    const dispatch = useDispatch();
    const { setAuthToken } = useJumboAuth();
    const { allOpExpensesData } = useSelector(state => state.typereducer);
    const { hideDialog, showDialog } = useJumboDialog();

    // URL Search Params for state retention
    const [searchParams, setSearchParams] = useSearchParams();

    // 1. Pagination State derived from URL
    const urlPage = parseInt(searchParams.get('page')) || 1;
    const [page, setPage] = React.useState(urlPage);
    const [offset, setOffset] = React.useState((urlPage - 1) * LIMIT);
    const [totalCount, setTotalCount] = React.useState(0);
    const [pageId, setPageId] = React.useState(offset + 1);

    // 2. Filter State derived from URL
    const initialFilterModel = React.useMemo(() => {
        // Construct filterModel from URL params prefixed with 'filter_'
        const filterItems = [];
        for (const [key, value] of searchParams.entries()) {
            if (key.startsWith('filter_')) {
                filterItems.push({
                    field: key.substring(7), // Remove 'filter_' prefix
                    operator: 'contains', // DataGridPro default operator for text filters
                    value: value,
                });
            }
        }
        return { items: filterItems };
    }, [searchParams]);

    const [queryOptions, setQueryOptions] = React.useState({ filterModel: initialFilterModel });


    // --- Data Processing ---

    // Memoize rows creation for performance
    const rows = React.useMemo(() => {
        let currentId = pageId;
        return allOpExpensesData.map((item) => ({
            id: currentId++,
            vendorName: item?.vendor_id?.vendor_name,
            tdsPercent: item?.vendor_id?.tds_amount,
            expenseId: item?.id,
            expenseAmount: item?.expense_amount,
            expenseDate: item?.expense_date ? moment(item?.expense_date).format('DD MMM YYYY') : '',
            expenseDescription: item?.expense_description || '--',
            expenseNote: item?.expense_note || '--',
            expenseDoc: item?.bill_reciept_documents || [], // Default to array for safer checking
            status: item?.status || 'Requested',
            hrFinanceStatus: item?.hr_finance_status || '--',
            paymentStatus: item?.payment_status || 'Unpaid',
            paidBy: item?.paid_by || '--',
            paymentDate: item?.payment_date ? moment(item?.payment_date).format('DD MMM YYYY') : '',
            actionBy: item?.action_by?.name || '--',
            addedBy: item?.created_by?.name || '--',
            actionRemark: item?.action_remark || '--',
            sgstAmount: item?.sgst_amount || '--',
            cgstAmount: item?.cgst_amount || '--',
            igstAmount: item?.igst_amount || '--',
            tdsAmount: item?.tds_amount || '--',
            totalGST: item?.total_gst || '--',
            amountWithTax: item?.amount_with_tax || '--',
            payableAmount: item?.payable_amount || '--', // Corrected bitwise OR to logical OR
            invoiceNumber: item?.invoice_number || '--',
            company: item?.company || '--',
            projects: item?.projects || '--',
        }));
    }, [allOpExpensesData, pageId]);


    // --- Column Definitions ---

    // Extracted common header render logic
    const renderHeader = (headerName) => () => (
        <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>
            <span>{headerName}</span>
        </div>
    );

    const getStatusChipColor = (status) => {
        switch (status) {
            case 'Requested': return 'blue';
            case 'Approved': return 'green';
            case 'Rejected': return 'red';
            default: return 'gray';
        }
    };

    const columns = React.useMemo(() => [
        {
            field: 'id',
            headerName: 'ID',
            sortable: false,
            hideable: false,
            filterable: false,
            flex: 100,
            maxWidth: 100,
            align: 'center',
            headerAlign: 'center',
            renderHeader: renderHeader('ID'),
        },
        {
            field: 'vendorName',
            headerName: 'Vendor Name',
            sortable: false,
            flex: 220,
            align: 'center',
            headerAlign: 'center',
            renderHeader: renderHeader('Vendor Name'),
        },
        {
            field: 'status',
            headerName: 'Manager Status',
            sortable: false,
            flex: 300,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div
                        style={{
                            width: 10,
                            height: 10,
                            backgroundColor: getStatusChipColor(params.value),
                            marginRight: 8,
                            borderRadius: '100%',
                        }}
                    ></div>
                    {params.value}
                </div>
            ),
            renderHeader: renderHeader('Manager Status'),
        },
        {
            field: 'invoiceNumber',
            headerName: 'Invoice No.',
            sortable: false,
            filterable: false,
            flex: 220,
            align: 'center',
            headerAlign: 'center',
            renderHeader: renderHeader('Invoice No.'),
        },
        {
            field: 'addedBy',
            headerName: 'Added By',
            sortable: false,
            filterable: false,
            flex: 220,
            align: 'center',
            headerAlign: 'center',
            renderHeader: renderHeader('Added By'),
        },
        {
            field: 'actionRemark',
            headerName: 'Remark',
            filterable: false,
            flex: 300,
            align: 'center',
            headerAlign: 'center',
            renderHeader: renderHeader('Remark'),
        },
        {
            field: 'actionBy',
            headerName: 'Approved By',
            sortable: false,
            filterable: false,
            flex: 220,
            align: 'center',
            headerAlign: 'center',
            renderHeader: renderHeader('Approved By'),
        },
        {
            field: 'expenseAmount',
            headerName: 'Expense Amount',
            flex: 220,
            align: 'center',
            headerAlign: 'center',
            renderHeader: renderHeader('Expense Amount'),
        },
        {
            field: 'expenseDate',
            headerName: 'Expense Date',
            filterable: false,
            flex: 220,
            align: 'center',
            headerAlign: 'center',
            renderHeader: renderHeader('Expense Date'),
        },
        {
            field: 'expenseDescription',
            headerName: 'Description',
            filterable: false,
            flex: 300,
            align: 'center',
            headerAlign: 'center',
            renderHeader: renderHeader('Description'),
        },
        {
            field: 'expenseNote',
            headerName: 'Note',
            filterable: false,
            sortable: false,
            flex: 300,
            align: 'center',
            headerAlign: 'center',
            renderHeader: renderHeader('Note'),
        },
        {
            field: 'paymentStatus',
            headerName: 'Payment Status',
            type: 'singleSelect',
            valueOptions: ['Paid', 'Unpaid'],
            flex: 300,
            align: 'center',
            headerAlign: 'center',
            renderHeader: renderHeader('Payment Status'),
        },
        {
            field: 'paymentDate',
            headerName: 'Payment Date',
            type: 'date',
            sortable: false,
            flex: 300,
            align: 'center',
            headerAlign: 'center',
            valueGetter: (params) => {
                const date = new Date(params.value);
                return isNaN(date.getTime()) ? '' : date;
            },
            renderHeader: renderHeader('Payment Date'),
        },
        {
            field: 'tdsPercent',
            headerName: 'TDS Percent',
            filterable: false,
            sortable: false,
            flex: 300,
            align: 'center',
            headerAlign: 'center',
            renderHeader: renderHeader('TDS Percent'),
        },
        {
            field: 'tdsAmount',
            headerName: 'TDS Amount',
            filterable: false,
            sortable: false,
            flex: 300,
            align: 'center',
            headerAlign: 'center',
            renderHeader: renderHeader('TDS Amount'),
        },
        {
            field: 'totalGST',
            headerName: 'Total GST',
            filterable: false,
            sortable: false,
            flex: 300,
            align: 'center',
            headerAlign: 'center',
            renderHeader: renderHeader('Total GST'),
        },
        {
            field: 'amountWithTax',
            headerName: 'Amount with tax',
            filterable: false,
            sortable: false,
            flex: 300,
            align: 'center',
            headerAlign: 'center',
            renderHeader: renderHeader('Amount with tax'),
        },
        {
            field: 'payableAmount',
            headerName: 'Payable Amount',
            filterable: false,
            sortable: false,
            flex: 300,
            align: 'center',
            headerAlign: 'center',
            renderHeader: renderHeader('Payable Amount'),
        },
        {
            field: 'company',
            headerName: 'Company',
            filterable: false,
            sortable: false,
            flex: 300,
            align: 'center',
            headerAlign: 'center',
            renderHeader: renderHeader('Company'),
        },
        {
            field: 'projects',
            headerName: 'Projects',
            filterable: false,
            sortable: false,
            flex: 300,
            align: 'center',
            headerAlign: 'center',
            renderHeader: renderHeader('Projects'),
        },
        {
            field: 'expenseDoc',
            headerName: 'Documents',
            filterable: false,
            sortable: false,
            flex: 300,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {params.row.expenseDoc.length > 0 && (
                        <ArticleIcon
                            onClick={() => showExpenseDialog(params.value)}
                            style={{ width: '20px', height: '20px', marginLeft: '5px' }}
                            color="action"
                        />
                    )}
                </div>
            ),
            renderHeader: renderHeader('Documents'),
        },
    ], []);


    // --- Handlers ---

    const showExpenseDialog = (expenseDoc) => {
        showDialog({
            title: "Expense Documents",
            content: <ExpenseDocumentBox expenseDoc={expenseDoc} />
        });
    }

    const handlePageChange = React.useCallback((event, value) => {
        const newOffset = (value - 1) * LIMIT;

        // Update URL
        setSearchParams(prev => {
            prev.set('page', value.toString());
            return prev;
        }, { replace: true });

        // Update local state
        setPage(value);
        setOffset(newOffset);
        setPageId(newOffset + 1);

    }, [setSearchParams]);

    const handleExport = () => {
        // Use the current filter model to build the filter query string
        const filterQuery = buildFilterString(queryOptions.filterModel);

        subTaskService.getAllOpExpensess(filterQuery).then((data) => {
            const mockData = data?.data?.data?.map((item, index) => ({
                "id": index + 1,
                "vendorName": item?.vendor_id?.vendor_name || '--',
                "vendorType": item?.vendor_id?.vendor_type || '--',
                "companyName": item?.vendor_id?.company_name || '--',
                "GSTNo": item?.vendor_id?.gst_no || '--',
                // Using regex for PAN/TAN check
                "PANNo": /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(item?.vendor_id?.pan_tan) ? item?.vendor_id?.pan_tan : '--',
                "TANNo": /^[A-Z]{4}[0-9]{5}[A-Z]{1}$/.test(item?.vendor_id?.pan_tan) ? item?.vendor_id?.pan_tan : '--',
                "billingDetails": item?.vendor_id?.billing_details || '--',
                "status": item?.status || 'Requested',
                "actionRemark": item?.action_remark || "--",
                "addedBy": item?.created_by?.name || '--',
                "paymentStatus": item?.payment_status || 'Unpaid',
                "paidBy": item?.paid_by || '--',
                "paymentDate": item?.payment_date ? moment(item?.payment_date).format('DD MMM YYYY') : '',
                "expenseAmount": item?.expense_amount || '--',
                "expenseDate": item?.expense_date ? moment(item?.expense_date).format('DD MMM YYYY') : '--',
                "expenseDescription": item?.expense_description || '--',
                "expenseNote": item?.expense_note || '--',
                "sgstAmount": item?.sgst_amount || '--',
                "cgstAmount": item?.cgst_amount || '--',
                "igstAmount": item?.igst_amount || '--',
                "tdsPercent": item?.vendor_id?.tds_amount || '--',
                "tdsAmount": item?.tds_amount || '--',
                "totalGST": item?.total_gst || '--',
                "amountWithTax": item?.amount_with_tax || '--',
                "payableAmount": item?.payable_amount || '--',
                "invoiceNumber": item?.invoice_number || '--',
                "company": item?.company || '--',
                "projects": item?.projects || '--',
                "expenseDoc": item?.bill_reciept_documents?.map((doc) =>
                    docBaseUrl + doc?.base_url + "/" + doc?.key
                ).join('\n') || '--', // Join documents with newline for CSV
            }));

            // Functions for CSV export (kept as is for functionality preservation)
            function convertToCSV(objArray) {
                var array = typeof objArray !== 'object' ? JSON.parse(objArray) : objArray;
                var csv = '';

                // Header generation
                var headerLine = '';
                for (var index in array[0]) {
                    if (headerLine !== '') headerLine += ',';
                    headerLine += index;
                }
                csv += headerLine + '\r\n';

                for (var i = 0; i < array.length; i++) {
                    var line = '';
                    for (var index in array[i]) {
                        if (line !== '') line += ',';
                        var fieldValue = array[i][index];

                        if (Array.isArray(fieldValue)) {
                            fieldValue = fieldValue.join('\n');
                        } else if (typeof fieldValue === 'string' && fieldValue.charAt(0) === '0') {
                            fieldValue = '"' + fieldValue + '"';
                        }

                        if (typeof fieldValue === 'string' && (fieldValue.includes(',') || fieldValue.includes('\n'))) {
                            fieldValue = '"' + fieldValue.replace(/"/g, '""') + '"';
                        }

                        line += fieldValue;
                    }
                    csv += line + '\r\n';
                }

                return csv;
            }

            function exportCSVFile(headers, items, fileTitle) {
                // Modified: items are already headers-inclusive based on your original logic
                // The provided 'convertToCSV' already handles header row generation from keys of the first item

                var jsonObject = JSON.stringify(items);
                var csv = convertToCSV(jsonObject);
                var exportedFilenmae = fileTitle + '.csv' || 'export.csv';

                var blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                if (navigator.msSaveBlob) {
                    navigator.msSaveBlob(blob, exportedFilenmae);
                } else {
                    var link = document.createElement("a");
                    if (link.download !== undefined) {
                        var url = URL.createObjectURL(blob);
                        link.setAttribute("href", url);
                        link.setAttribute("download", exportedFilenmae);
                        link.style.visibility = 'hidden';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                    }
                }
            }

            // Using keys from the first mockData item as headers for consistency with exportCSVFile
            const headerKeys = mockData.length > 0 ? Object.keys(mockData[0]) : [];
            const headers = headerKeys.reduce((acc, key) => {
                // Map keys to readable header names
                const readableName = columns.find(col => col.field === key)?.headerName ||
                    key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
                acc[key] = readableName;
                return acc;
            }, {});

            const exportItems = mockData.map(item => {
                const newObj = {};
                for (const key of headerKeys) {
                    newObj[headers[key]] = item[key]; // Map data to use readable headers as keys
                }
                return newObj;
            });


            var fileTitle = 'Operational Expense Export';
            exportCSVFile(headers, exportItems, fileTitle); // Passing headers and items (which includes the data)

        }).catch((err) => {
            if (err?.response?.status === 401) {
                setAuthToken(null);
            } else {
                Swal.fire('Error', 'Failed to export data.', 'error');
            }
        });
    }

    const onFilterChange = React.useCallback((filterModel) => {
        setQueryOptions({ filterModel: { ...filterModel } });

        // Update URL parameters
        setSearchParams(prev => {
            // Remove all previous filter params
            for (const key of Array.from(prev.keys())) {
                if (key.startsWith('filter_')) {
                    prev.delete(key);
                }
            }

            // Add new filter params
            if (filterModel.items) {
                filterModel.items.forEach(item => {
                    if (item.field) {
                        prev.set(`filter_${item.field}`, item.value || '');
                    }
                });
            }

            // Reset page to 1 if filter is applied/changed
            if (filterModel.items?.length > 0) {
                prev.set('page', '1');
            } else {
                prev.delete('page'); // Clear page if no filters
            }

            return prev;
        }, { replace: true });

        // Reset pagination state immediately if filter changed
        // This will also trigger the useEffect
        if (filterModel.items.length > 0) {
            setOffset(0);
            setPage(1);
            setPageId(1);
        }

    }, [setSearchParams]);

    const handleCellClick = (params) => {
        const columnId = params.field;
        // Only show tooltip for data columns, not for 'id', 'checkbox', or 'documents'
        if (columnId !== 'id' && columnId !== '__check__' && columnId !== 'expenseDoc') {
            const cellValue = params.value;

            Swal.fire({
                html: `<div style="white-space: pre-wrap;">${cellValue}</div>`,
                showConfirmButton: false,
                customClass: {
                    popup: 'my-custom-size',
                },
                didOpen: () => {
                    const swalPopup = Swal.getPopup();
                    if (swalPopup) {
                        swalPopup.style.width = '400px';
                    }
                },
            });
        }
    };


    // --- Data Fetching Effect ---

    React.useEffect(() => {
        const filterString = buildFilterString(queryOptions.filterModel);

        subTaskService.getAllOpExpensess(offset, LIMIT, filterString)
            .then((data) => {
                setTotalCount(data?.data?.count || 0);
                dispatch(getAllOpExpensesData(data?.data?.data || []))
            })
            .catch((err) => {
                if (err?.response?.status === 401) {
                    setAuthToken(null);
                }
            });

        return () => {
            dispatch(getAllOpExpensessCleanUp())
        }
    }, [offset, queryOptions, dispatch, setAuthToken]);


    // --- Render ---

    const totalPages = Math.ceil(totalCount / LIMIT);

    return (
        <>
            <div style={{ height: '60px', width: '100%', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                <Button onClick={handleExport} variant="contained">
                    Export Expenses
                </Button>
            </div>
            <div style={{ height: '100%', width: '100%' }}>

                <DataGridPro sx={{ backgroundColor: '#F6F4FA' }}
                    rows={rows}
                    columns={columns}
                    pageSize={30} // pageSize here only affects visible rows, not API limit (which is 10)
                    autoPageSize={true}
                    autoHeight={true}
                    density='standard'
                    filterMode='server'
                    filterModel={queryOptions.filterModel}
                    onFilterModelChange={onFilterChange}
                    hideFooter={true}
                    onCellClick={handleCellClick}
                />
                {totalPages > 1 && (
                    <Pagination
                        count={totalPages}
                        sx={{ mt: 3, float: 'right', color: "#F6F4FA" }}
                        page={page}
                        onChange={handlePageChange}
                    />
                )}
            </div>
        </>
    );
}